'use client';

import React, { useState, useRef, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Image from 'next/image';
import TrustpilotWidget from '@/components/TrustpilotWidget';

const whyCards = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: 'Safety',
    text: 'Properly aligned crumple zones and chassis geometry ensure the car performs correctly in future impacts.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 8v4l3 3"/>
      </svg>
    ),
    title: 'Handling',
    text: 'Eliminates pulling, uneven tyre wear, and steering issues caused by chassis distortion.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
      </svg>
    ),
    title: 'ADAS Calibration',
    text: 'Cameras, radar, and safety sensors depend on precise vehicle geometry to function correctly.',
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
        <line x1="12" y1="1" x2="12" y2="23"/>
        <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>
      </svg>
    ),
    title: 'Resale Value',
    text: "Documented factory-standard repair maintains your vehicle\'s value and insurability.",
  },
];

const whenItems = [
  'After any serious collision or high-speed impact',
  'Suspension damage or component displacement',
  'Misaligned panels or uneven body gaps',
  'Vehicle pulling to one side after a repair',
  'Premium or performance vehicles requiring exact tolerances',
];

const processSteps = [
  {
    num: '01',
    title: 'Vehicle Mounted on Car-O-Liner Bench',
    text: 'The car is secured and referenced against our factory data library.',
  },
  {
    num: '02',
    title: 'Computerised Measurement',
    text: 'Every structural dimension is measured and compared to OEM specifications.',
  },
  {
    num: '03',
    title: 'Precision Alignment',
    text: 'Using manufacturer-approved pulling and alignment procedures, we correct all deviations.',
  },
  {
    num: '04',
    title: 'Verification & Printout',
    text: 'A final computerised report confirms all measurements are within factory tolerance — you get a copy.',
  },
];

const brands = [
  'Mercedes-Benz',
  'BMW',
  'Audi',
  'Volkswagen',
  'Porsche',
  'SEAT',
  'Škoda',
  'All Makes & Models',
];

/* ── Shared geometric background style ── */
const geoBg = {
  background: 'linear-gradient(135deg, #0a0a0a 0%, #121212 50%, #0d0d0d 100%)',
};

const geoTexture = {
  backgroundImage: `repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 28px,
    rgba(255,255,255,0.018) 28px,
    rgba(255,255,255,0.018) 29px
  )`,
};

const altBg = {
  background: 'linear-gradient(135deg, #0d0d0d 0%, #111111 100%)',
};

/* ── Diagonal section divider ── */
function GeoDivider() {
  return (
    <div className="relative h-px overflow-visible" aria-hidden="true">
      <div
        className="absolute inset-0"
        style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(204,26,26,0.3) 30%, rgba(204,26,26,0.3) 70%, transparent 100%)' }}
      />
      <div
        className="absolute left-1/2 -translate-x-1/2 -top-2 w-4 h-4"
        style={{
          background: '#CC1A1A',
          clipPath: 'polygon(50% 0%, 100% 100%, 0% 100%)',
          opacity: 0.5,
        }}
      />
    </div>
  );
}

export default function ChassisAlignmentPage() {
  // ── Booking form state ──────────────────────────────────────────────────────
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    registration: '',
    vehicleMake: '',
    damage: '',
    website: '',
  });
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [showModal, setShowModal] = useState(false);
  const [bookingReference, setBookingReference] = useState('');
  const [rateLimitMsg, setRateLimitMsg] = useState('');
  const lastSubmitRef = useRef<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateField = (name: string, value: string): string => {
    if (name === 'name') {
      if (!value.trim()) return 'Full name is required';
      if (value.trim().length < 2) return 'Name must be at least 2 characters';
    }
    if (name === 'phone') {
      if (!value.trim()) return 'Phone number is required';
      const stripped = value.replace(/[\s\-().]/g, '');
      if (!/^(\+44|0044|0)[1-9]\d{8,9}$/.test(stripped)) return 'Enter a valid UK phone number';
    }
    if (name === 'registration') {
      if (!value.trim()) return 'Vehicle registration is required';
    }
    if (name === 'vehicleMake') {
      if (!value.trim()) return 'Vehicle make & model is required';
    }
    if (name === 'email' && value.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Enter a valid email address';
    }
    return '';
  };

  const handleFieldChange = (name: string, value: string) => {
    setFormData(p => ({ ...p, [name]: name === 'registration' ? value.toUpperCase() : value }));
    if (touched[name]) {
      const err = validateField(name, value);
      setFieldErrors(prev => { const next = { ...prev }; if (err) next[name] = err; else delete next[name]; return next; });
    }
  };

  const handleFieldBlur = (name: string, value: string) => {
    setTouched(prev => ({ ...prev, [name]: true }));
    const err = validateField(name, value);
    setFieldErrors(prev => { const next = { ...prev }; if (err) next[name] = err; else delete next[name]; return next; });
  };

  const inputClass = (name: string) => {
    const base = 'w-full px-4 py-3 bg-[#1a1a1a] border text-white placeholder-white/30 font-dm-sans text-sm focus:outline-none transition-colors rounded-sm';
    if (touched[name] && fieldErrors[name]) return `${base} border-red-500 focus:border-red-500`;
    if (touched[name] && !fieldErrors[name]) return `${base} border-green-600/60 focus:border-green-600/60`;
    return `${base} border-white/10 focus:border-[#CC1A1A]/60`;
  };

  const handlePhotoChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter(f => f.size <= 5 * 1024 * 1024 && (f.type === 'image/jpeg' || f.type === 'image/png'));
    const combined = [...photos, ...valid].slice(0, 3);
    setPhotos(combined);
    const previews = combined.map(f => URL.createObjectURL(f));
    setPhotoPreviews(previews);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [photos]);

  const removePhoto = useCallback((idx: number) => {
    const updated = photos.filter((_, i) => i !== idx);
    setPhotos(updated);
    setPhotoPreviews(updated.map(f => URL.createObjectURL(f)));
  }, [photos]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ── Rate limiting ────────────────────────────────────────────────────────
    const now = Date.now();
    if (now - lastSubmitRef.current < 60000) {
      const remaining = Math.ceil((60000 - (now - lastSubmitRef.current)) / 1000);
      setRateLimitMsg(`Please wait ${remaining} seconds before submitting again.`);
      return;
    }
    setRateLimitMsg('');

    // ── Honeypot check ───────────────────────────────────────────────────────
    if (formData.website) {
      setShowModal(true);
      return;
    }

    // Validate all required fields
    const requiredFields = ['name', 'phone', 'registration', 'vehicleMake'];
    const allTouched: Record<string, boolean> = {};
    const errors: Record<string, string> = {};
    requiredFields.forEach(f => {
      allTouched[f] = true;
      const err = validateField(f, formData[f as keyof typeof formData] as string);
      if (err) errors[f] = err;
    });
    if (formData.email.trim()) {
      const emailErr = validateField('email', formData.email);
      if (emailErr) { errors.email = emailErr; allTouched.email = true; }
    }
    setTouched(prev => ({ ...prev, ...allTouched }));
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setSubmitting(true);
    setFormError('');
    lastSubmitRef.current = Date.now();

    const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL;
    const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

    // ── Generate booking reference ────────────────────────────────────────────
    const year = new Date().getFullYear();
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const rand = Array.from({ length: 6 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    const bookingRef = `NLB-${year}-${rand}`;

    // ── Save to Supabase (independent of email) ──────────────────────────────
    try {
      const { createClient } = await import('@/lib/supabase/client');
      const supabase = createClient();
      await supabase.from('bookings').insert({
        service_type: 'chassis-alignment',
        name: formData.name,
        phone: formData.phone,
        email: formData.email || null,
        vehicle_reg: formData.registration.toUpperCase(),
        vehicle_make_model: formData.vehicleMake,
        message: formData.damage || null,
        photo_urls: null,
        status: 'new',
        reference: bookingRef,
      });
    } catch {
      // Supabase save failure must not block email
    }

    // ── Send email (independent of Supabase) ─────────────────────────────────
    try {
      const body = new FormData();
      body.append('name', formData.name);
      body.append('phone', formData.phone);
      body.append('email', formData.email);
      body.append('registration', formData.registration.toUpperCase());
      body.append('vehicleMake', formData.vehicleMake);
      body.append('damage', formData.damage);
      body.append('service', 'Chassis Alignment');
      body.append('subject', `New Chassis Alignment Booking – ${formData.name} – ${formData.registration.toUpperCase()}`);
      body.append('successMessage', "Thank you! We'll call you within 1 hour to schedule your free assessment.");
      body.append('bookingRef', bookingRef);
      photos.forEach((photo, i) => body.append(`photo_${i}`, photo, photo.name));

      const res = await fetch(`${SUPABASE_URL}/functions/v1/send-booking-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
        body,
      });
      if (!res.ok) throw new Error('Email failed');
    } catch {
      // Email failure must not block success if Supabase saved
    }

    setFormData({ name: '', phone: '', email: '', registration: '', vehicleMake: '', damage: '', website: '' });
    setPhotos([]);
    setPhotoPreviews([]);
    setSubmitting(false);
    setBookingReference(bookingRef);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    if (typeof window !== 'undefined') {
      window.location.href = `/booking-confirmed?service=chassis-alignment&ref=${encodeURIComponent(bookingReference)}`;
    }
  };

  return (
    <>
      {/* ─── SUCCESS MODAL ─── */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0,0,0,0.75)' }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-heading-ca"
        >
          <div
            className="relative w-full max-w-md rounded-sm p-8 flex flex-col items-center text-center"
            style={{ background: '#121212', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
              style={{ background: 'rgba(22,163,74,0.15)', border: '2px solid rgba(22,163,74,0.5)' }}
              aria-hidden="true"
            >
              <svg
                width="40" height="40" viewBox="0 0 24 24" fill="none"
                stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                style={{ animation: 'checkPop 0.4s ease-out' }}
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h2 id="modal-heading-ca" className="font-dm-sans font-black text-white text-2xl mb-3">
              Booking Received!
            </h2>
            <p className="font-dm-sans text-white/60 text-[15px] leading-relaxed mb-4">
              Thank you! We&apos;ll call you within 1 hour to schedule your free assessment.
            </p>
            {bookingReference && (
              <div
                className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-sm"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                <span className="font-dm-sans text-[11px] uppercase tracking-[0.2em] text-white/35">Reference</span>
                <span className="font-dm-sans text-[13px] font-bold text-white/80 tracking-wider">{bookingReference}</span>
              </div>
            )}
            <button
              onClick={handleCloseModal}
              className="w-full py-3 font-dm-sans font-bold text-[13px] uppercase tracking-[0.15em] text-white transition-all duration-200 rounded-sm"
              style={{ background: '#CC1A1A' }}
              onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
              onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
            >
              Close
            </button>
          </div>
        </div>
      )}
      <style>{`@keyframes checkPop { 0% { transform: scale(0.5); opacity: 0; } 70% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }`}</style>
      <Header />
      <main className="min-h-screen" style={geoBg}>
        {/* Global geometric texture overlay */}
        <div className="fixed inset-0 pointer-events-none z-0" style={geoTexture} aria-hidden="true" />

        {/* HERO SECTION */}
        <section className="relative pt-32 pb-20 px-6 overflow-hidden z-10">
          {/* Red radial glow */}
          <div
            className="absolute inset-0 pointer-events-none"
            aria-hidden="true"
            style={{ background: 'radial-gradient(ellipse 60% 80% at 80% 50%, rgba(204,26,26,0.07) 0%, transparent 70%)' }}
          />

          <div className="relative max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Left: Text — with red left-border accent */}
              <div
                className="pl-6"
                style={{ borderLeft: '3px solid #CC1A1A' }}
              >
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 border border-[#CC1A1A]/40 bg-[#CC1A1A]/10 mb-8">
                  <span className="w-2 h-2 rounded-full bg-[#CC1A1A] animate-pulse" />
                  <span className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A]">
                    Car-O-Liner Certified
                  </span>
                </div>

                <h1 className="font-fraunces font-bold text-white text-4xl md:text-5xl lg:text-6xl leading-[1.1] mb-3 tracking-tight">
                  Factory-Spec Structural Repairs Using{' '}
                  <em className="text-[#CC1A1A] not-italic">Car-O-Liner</em>{' '}Technology
                </h1>
                {/* Red underline accent */}
                <div className="w-16 h-[3px] bg-[#CC1A1A] mb-6" />

                <p className="font-dm-sans text-[#C0C0C0] text-lg leading-relaxed mb-10 max-w-xl">
                  We restore your vehicle&apos;s chassis geometry to exact manufacturer specifications — critical for safety, handling, and ADAS calibration.
                </p>

                {/* CTAs */}
                <div className="flex flex-wrap gap-4 mb-10">
                  <a
                    href="#booking-form"
                    onClick={e => { e.preventDefault(); document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' }); }}
                    className="inline-flex items-center gap-2 px-8 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                    style={{ background: '#CC1A1A', borderRadius: '3px' }}
                    onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
                    onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
                  >
                    Book Free Assessment
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </a>
                  <a
                    href="tel:01614001687"
                    className="inline-flex items-center gap-2 px-8 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                    style={{ background: '#1a1a1a', border: '1px solid #CC1A1A', borderRadius: '3px' }}
                    onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(204,26,26,0.7)')}
                    onMouseLeave={e => (e.currentTarget.style.borderColor = '#CC1A1A')}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                    </svg>
                    01614001687
                  </a>
                </div>
              </div>

              {/* Right: Image — keep existing illustration */}
              <div className="relative">
                <div className="aspect-[4/3] bg-[#111111] border border-white/[0.08] overflow-hidden" style={{ aspectRatio: '4/3' }}>
                  <Image
                    src="/assets/images/chassis_alignment_hero.png"
                    alt="Car-O-Liner precision chassis alignment bench in a professional automotive workshop"
                    width={800}
                    height={600}
                    className="w-full h-full object-cover"
                    priority
                  />
                </div>
                {/* Crimson accent line */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#CC1A1A]" />
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 1 — What is Car-O-Liner? */}
        <section className="py-20 px-6 relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Precision Technology</p>
                <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                  What is Car-O-Liner?
                </h2>
                <div className="w-12 h-[3px] bg-[#CC1A1A] mb-6" />
                <p className="font-dm-sans text-[#C0C0C0] text-base leading-relaxed">
                  Car-O-Liner is a precision measuring and repair system used by manufacturer-approved bodyshops worldwide. Unlike general repair shops that might estimate alignment visually, we measure every chassis dimension against factory data — ensuring your car is restored to exactly how it left the production line. It&apos;s the same standard used by main dealers and approved repairers.
                </p>
              </div>

              {/* Certification card */}
              <div className="bg-[#111111] border border-[#CC1A1A]/30 p-8">
                <div className="flex items-start gap-6">
                  <div className="shrink-0 w-16 h-16 bg-[#CC1A1A]/10 border border-[#CC1A1A]/30 flex items-center justify-center">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="1.5" strokeLinecap="round">
                      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                      <path d="M9 12l2 2 4-4"/>
                    </svg>
                  </div>
                  <div>
                    <p className="font-dm-sans text-xs font-bold tracking-[0.2em] uppercase text-[#CC1A1A] mb-2">Car-O-Liner Certified</p>
                    <p className="font-fraunces font-light text-white text-xl mb-3">Manufacturer-Approved Standard</p>
                    <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">
                      The same precision system used by main dealers and OEM-approved repairers. Every measurement verified against factory data.
                    </p>
                  </div>
                </div>
                <div className="mt-6 pt-6 border-t border-white/[0.06] grid grid-cols-3 gap-4">
                  {['Factory Data', 'OEM Specs', 'Certified']?.map((label) => (
                    <div key={label} className="text-center">
                      <div className="w-2 h-2 rounded-full bg-[#CC1A1A] mx-auto mb-2" />
                      <p className="font-dm-sans text-[#E0E0E0] text-xs uppercase tracking-wider">{label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 2 — Why structural alignment matters */}
        <section className="py-20 px-6 relative z-10" style={geoBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Critical for Every Vehicle</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Why structural alignment matters
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {whyCards?.map((card) => (
                <div key={card?.title} className="bg-[#111111] border border-white/[0.08] p-7 hover:border-[#CC1A1A]/30 transition-colors duration-300">
                  <div className="w-12 h-12 bg-[#CC1A1A]/10 flex items-center justify-center mb-5">
                    {card?.icon}
                  </div>
                  <h3 className="font-fraunces font-semibold text-white text-xl mb-3">{card?.title}</h3>
                  <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">{card?.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 3 — When you need chassis alignment */}
        <section className="py-20 px-6 relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-start">
              <div>
                <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Indicators</p>
                <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                  When you need it
                </h2>
                <div className="w-12 h-[3px] bg-[#CC1A1A] mb-6" />
                <p className="font-dm-sans text-[#C0C0C0] text-base leading-relaxed">
                  If any of these apply to your vehicle, a chassis alignment check is essential.
                </p>
              </div>

              <div className="space-y-4">
                {whenItems?.map((item, i) => (
                  <div key={i} className="flex items-start gap-4 bg-[#111111] border border-white/[0.08] px-6 py-5 hover:border-[#CC1A1A]/20 transition-colors">
                    <div className="shrink-0 mt-0.5">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M20 6L9 17l-5-5"/>
                      </svg>
                    </div>
                    <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 4 — Our process */}
        <section className="py-20 px-6 relative z-10" style={geoBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Step by Step</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Our process
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto" />
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {processSteps?.map((step) => (
                <div key={step?.num} className="bg-[#111111] border border-white/[0.08] p-7 relative hover:border-[#CC1A1A]/30 transition-colors duration-300">
                  <div className="font-fraunces text-5xl font-light text-[#CC1A1A] mb-5 leading-none">{step?.num}</div>
                  <h3 className="font-fraunces font-semibold text-white text-lg mb-3 leading-snug">{step?.title}</h3>
                  <p className="font-dm-sans text-[#C0C0C0] text-sm leading-relaxed">{step?.text}</p>
                  <div className="absolute bottom-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#CC1A1A] to-transparent" />
                </div>
              ))}
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 5 — Brands we work with */}
        <section className="py-20 px-6 overflow-hidden relative z-10" style={altBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">All Makes Welcome</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Brands we work with
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto mb-4" />
              <p className="font-dm-sans text-[#C0C0C0] text-base">
                We work to manufacturer data for every brand, not generic estimates.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {brands?.map((brand) => (
                <div
                  key={brand}
                  className={`bg-[#111111] border px-6 py-5 flex items-center justify-center text-center ${
                    brand === 'All Makes & Models' ? 'border-[#CC1A1A]/40 bg-[#CC1A1A]/5' : 'border-white/[0.08] hover:border-[#CC1A1A]/20'
                  } transition-colors duration-300`}
                >
                  <span
                    className={`font-dm-sans font-semibold text-sm tracking-wide ${
                      brand === 'All Makes & Models' ? 'text-[#CC1A1A]' : 'text-[#E0E0E0]'
                    }`}
                  >
                    {brand}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* SECTION 6 — Before/After */}
        <section className="py-20 px-6 relative z-10" style={geoBg}>
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Proof of Quality</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Structural repairs, done right
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mx-auto mb-4" />
              <p className="font-dm-sans text-[#C0C0C0] text-base">
                Every repair is verified against factory data — not guesswork.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Before */}
              <div className="relative">
                <div className="aspect-[4/3] bg-[#111111] border border-white/[0.08] flex flex-col items-center justify-center gap-4">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <path d="M21 15l-5-5L5 21"/>
                  </svg>
                  <p className="font-dm-sans text-white/20 text-xs tracking-wider uppercase">Before Repair Photo</p>
                </div>
                <div className="absolute top-4 left-4 px-3 py-1 bg-[#CC1A1A] font-dm-sans text-white text-xs font-bold uppercase tracking-widest">
                  Before
                </div>
              </div>

              {/* After */}
              <div className="relative">
                <div className="aspect-[4/3] bg-[#111111] border border-white/[0.08] flex flex-col items-center justify-center gap-4">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1" strokeLinecap="round" opacity="0.2">
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                    <circle cx="8.5" cy="8.5" r="1.5"/>
                    <path d="M21 15l-5-5L5 21"/>
                  </svg>
                  <p className="font-dm-sans text-white/20 text-xs tracking-wider uppercase">After Repair Photo</p>
                </div>
                <div className="absolute top-4 left-4 px-3 py-1 bg-[#161616] border border-white/20 font-dm-sans text-white text-xs font-bold uppercase tracking-widest">
                  After
                </div>
              </div>
            </div>
          </div>
        </section>

        <GeoDivider />

        {/* CTA SECTION */}
        <section id="contact" className="py-24 px-6 relative z-10" style={altBg}>
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-6">Free Assessment</p>
            <h2 className="font-fraunces font-bold text-white text-3xl md:text-5xl leading-tight mb-2">
              Had a collision? Book a free structural assessment.
            </h2>
            <div className="w-16 h-[3px] bg-[#CC1A1A] mx-auto mb-6" />
            <p className="font-dm-sans text-[#C0C0C0] text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
              We&apos;ll inspect your vehicle, measure the chassis, and tell you exactly what&apos;s needed — at no cost.
            </p>

            <div className="flex flex-wrap gap-4 justify-center mb-10">
              <a
                href="#booking-form"
                onClick={e => { e.preventDefault(); document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 px-10 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                style={{ background: '#CC1A1A', borderRadius: '3px' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#E02020')}
                onMouseLeave={e => (e.currentTarget.style.background = '#CC1A1A')}
              >
                Book Free Assessment
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </a>
              <a
                href="tel:01614001687"
                className="inline-flex items-center gap-2 px-10 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200"
                style={{ background: '#1a1a1a', border: '1px solid #CC1A1A', borderRadius: '3px' }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(204,26,26,0.7)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = '#CC1A1A')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
                </svg>
                01614001687
              </a>
            </div>

            <a
              href="mailto:contactus@nolimitauto.co.uk"
              className="font-dm-sans text-white/40 text-sm hover:text-[#CC1A1A] transition-colors"
            >
              contactus@nolimitauto.co.uk
            </a>
          </div>
        </section>

        <GeoDivider />

        {/* ── BOOKING FORM SECTION ── */}
        {/* Trustpilot Mini Widget */}
        <div style={{ background: '#121212', padding: '48px 24px 0' }}>
          <div className="max-w-2xl mx-auto">
            <TrustpilotWidget templateId="53aa8807dec7e10d38f59f32" height="350px" />
          </div>
        </div>
        <section id="booking-form" className="py-20 px-6 relative z-10" style={{ background: '#121212' }}>
          <div className="max-w-2xl mx-auto">
            {/* Heading */}
            <div className="mb-10">
              <p className="font-dm-sans text-xs font-bold tracking-[0.25em] uppercase text-[#CC1A1A] mb-4">Free Assessment</p>
              <h2 className="font-fraunces font-bold text-white text-3xl md:text-4xl leading-tight mb-2">
                Book Your Free Assessment
              </h2>
              <div className="w-12 h-[3px] bg-[#CC1A1A] mb-4" />
              <p className="font-dm-sans text-[#C0C0C0] text-base">
                Fill in the form below and we&apos;ll call you within 1 hour to schedule your assessment.
              </p>
            </div>

            {submitted ? (
              <div className="bg-[#1a1a1a] border border-[#CC1A1A]/40 rounded p-8 text-center">
                <div className="w-14 h-14 bg-[#CC1A1A]/10 border border-[#CC1A1A]/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#CC1A1A" strokeWidth="2" strokeLinecap="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <p className="font-dm-sans font-bold text-white text-lg mb-2">Booking Received!</p>
                <p className="font-dm-sans text-[#C0C0C0] text-base">
                  Thank you! We&apos;ll call you within 1 hour to schedule your free assessment.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                {/* Honeypot — hidden from real users, visible to bots */}
                <div
                  style={{ position: 'absolute', left: '-9999px', top: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}
                  aria-hidden="true"
                >
                  <label htmlFor="website-hp-ca">Website</label>
                  <input
                    id="website-hp-ca"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website}
                    onChange={e => setFormData(p => ({ ...p, website: e.target.value }))}
                  />
                </div>
                {/* Name */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Full Name <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={e => handleFieldChange('name', e.target.value)}
                    onBlur={e => handleFieldBlur('name', e.target.value)}
                    placeholder="Your full name"
                    className={inputClass('name')}
                  />
                  {touched.name && fieldErrors.name && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.name}</p>
                  )}
                </div>
                {/* Phone */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Phone Number <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={e => handleFieldChange('phone', e.target.value)}
                    onBlur={e => handleFieldBlur('phone', e.target.value)}
                    placeholder="e.g. 07700 900000"
                    className={inputClass('phone')}
                  />
                  {touched.phone && fieldErrors.phone && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.phone}</p>
                  )}
                </div>
                {/* Email */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Email Address <span className="text-white/30 font-normal">(optional — for confirmation)</span>
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => handleFieldChange('email', e.target.value)}
                    onBlur={e => handleFieldBlur('email', e.target.value)}
                    placeholder="your@email.com"
                    className={inputClass('email')}
                  />
                  {touched.email && fieldErrors.email && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.email}</p>
                  )}
                </div>
                {/* Registration */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Vehicle Registration <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.registration}
                    onChange={e => handleFieldChange('registration', e.target.value)}
                    onBlur={e => handleFieldBlur('registration', e.target.value)}
                    placeholder="e.g. AB12 CDE"
                    className={`${inputClass('registration')} uppercase`}
                  />
                  {touched.registration && fieldErrors.registration && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.registration}</p>
                  )}
                </div>
                {/* Vehicle Make/Model */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Vehicle Make &amp; Model <span className="text-[#CC1A1A]">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.vehicleMake}
                    onChange={e => handleFieldChange('vehicleMake', e.target.value)}
                    onBlur={e => handleFieldBlur('vehicleMake', e.target.value)}
                    placeholder="e.g. Mercedes C-Class 2020"
                    className={inputClass('vehicleMake')}
                  />
                  {touched.vehicleMake && fieldErrors.vehicleMake && (
                    <p className="mt-1.5 font-dm-sans text-[12px] text-red-400">{fieldErrors.vehicleMake}</p>
                  )}
                </div>
                {/* Photo Upload */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Upload photos of the damage <span className="text-white/30 font-normal">(optional, up to 3)</span>
                  </label>
                  <div
                    className="border border-dashed border-white/20 bg-[#1a1a1a] rounded-sm p-6 text-center cursor-pointer hover:border-[#CC1A1A]/40 transition-colors"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" className="mx-auto mb-2">
                      <path d="M21 15v4a2 2 0 01-2 2H9a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/>
                    </svg>
                    <p className="font-dm-sans text-white/40 text-sm">Click to upload photos (JPG/PNG, max 5MB each)</p>
                    <p className="font-dm-sans text-white/25 text-xs mt-1">{photos.length}/3 photos selected</p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png"
                    multiple
                    className="hidden"
                    onChange={handlePhotoChange}
                    disabled={photos.length >= 3}
                  />
                  {photoPreviews.length > 0 && (
                    <div className="flex gap-3 mt-3 flex-wrap">
                      {photoPreviews.map((src, i) => (
                        <div key={i} className="relative w-20 h-20 border border-white/10 rounded-sm overflow-hidden">
                          <img src={src} alt={`Preview ${i + 1}`} className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removePhoto(i)}
                            className="absolute top-1 right-1 w-5 h-5 bg-[#CC1A1A] rounded-full flex items-center justify-center"
                          >
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                              <path d="M18 6L6 18M6 6l12 12"/>
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Damage Description */}
                <div>
                  <label className="block font-dm-sans text-sm font-semibold text-white mb-2">
                    Describe the damage <span className="text-white/30 font-normal">(optional)</span>
                  </label>
                  <textarea
                    rows={4}
                    value={formData.damage}
                    onChange={e => setFormData(p => ({ ...p, damage: e.target.value }))}
                    placeholder="Tell us about the issue..."
                    className="w-full px-4 py-3 bg-[#1a1a1a] border border-white/10 text-white placeholder-white/30 font-dm-sans text-sm focus:outline-none focus:border-[#CC1A1A]/60 transition-colors rounded-sm resize-none"
                  />
                </div>

                {formError && (
                  <p className="font-dm-sans text-sm text-red-400 bg-red-900/20 border border-red-500/30 px-4 py-3 rounded-sm">{formError}</p>
                )}
                {rateLimitMsg && (
                  <p className="font-dm-sans text-sm text-yellow-400 bg-yellow-900/20 border border-yellow-500/30 px-4 py-3 rounded-sm">{rateLimitMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full flex items-center justify-center gap-3 px-8 py-4 text-white font-dm-sans font-bold text-sm uppercase tracking-widest transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed"
                  style={{ background: '#CC1A1A', borderRadius: '3px' }}
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 12a9 9 0 11-6.219-8.56"/>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>Book Free Assessment →</>
                  )}
                </button>
              </form>
            )}
          </div>
        </section>

      </main>
      <Footer />
    </>
  );
}
